import { Notification, SocketNotification } from "@lib/types/Notification";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  apiGetNotifications,
  apiMarkAllNotificationsAsRead,
  apiMarkNotificationAsRead,
} from "@lib/api/notification";
import { ApiParams, PaginationType } from "@lib/types/Api";
import { generateUniqueID } from "@lib/helpers/StringHelper";
import { RootState } from "..";
interface ISettings {
  data: Notification[];
  loading: boolean;
  error: string | null;
  unseenCount: number;
}

const initialState: ISettings = {
  data: [],
  loading: false,
  error: null,
  unseenCount: 0,
};

export const markAllNotificationsAsRead = createAsyncThunk(
  "notifications/markAllNotificationsAsRead",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const ids = state.notifications.data
        .filter((notification) => !notification.isResolved)
        .map((notification) => notification.id);
      const response = await apiMarkAllNotificationsAsRead(ids);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to mark all notifications as read",
      );
    }
  },
);

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markNotificationAsRead",
  async (id: string, { rejectWithValue }) => {
    try {
      await apiMarkNotificationAsRead(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to mark notification as read",
      );
    }
  },
);

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (params: ApiParams, { rejectWithValue }) => {
    try {
      const response = await apiGetNotifications({
        sort: "createdAt,DESC",
        limit: 10,
        ...params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch notifications",
      );
    }
  },
);

const slice = createSlice({
  name: "notifications",
  initialState: initialState,
  reducers: {
    markAsSeen: (state, action: PayloadAction<string>) => {
      const notification = state.data.find((n) => n.id === action.payload);
      if (notification && !notification.isResolved) {
        notification.isResolved = true;
        state.unseenCount = Math.max(0, state.unseenCount - 1);
      }
    },
    markAllAsSeen: (state) => {
      state.data.forEach((notification) => {
        notification.isResolved = true;
      });
      state.unseenCount = 0;
    },
    addNewNotification: (state, action: PayloadAction<Notification>) => {
      state.data.unshift({
        isResolved: false,
        createdAt: new Date().toISOString(),
        tempId: generateUniqueID(),
        ...action.payload,
      });
      state.unseenCount += 1;
    },
    addNewSocketNotification: (
      state,
      action: PayloadAction<SocketNotification>,
    ) => {
      state.data.unshift({
        isResolved: false,
        createdAt: new Date().toISOString(),
        ...action.payload,
      });
      state.unseenCount += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<PaginationType<Notification>>) => {
          state.loading = false;
          state.data = action.payload.data.map((notification) => ({
            ...notification,
          }));
          state.unseenCount = action.payload.data.filter(
            (notification) => !notification.isResolved,
          ).length;
        },
      )
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(
        markNotificationAsRead.fulfilled,
        (state, action: PayloadAction<string>) => {
          const notification = state.data.find(
            (n) => n?.id === action?.payload,
          );
          if (notification) {
            notification.isResolved = true;
            state.unseenCount = state.data.filter(
              (notification) => !notification.isResolved,
            ).length;
          }
        },
      )
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.data.forEach((notification) => {
          notification.isResolved = true;
        });
        state.unseenCount = 0;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default slice.reducer;
export const {
  markAsSeen,
  markAllAsSeen,
  addNewNotification,
  addNewSocketNotification,
} = slice.actions;
